(function () {
  'use strict';

  const { subEvents, PUB_SUB_EVENTS, fetchConfig } = window.Universe;

  class ProductForms {
    constructor() {
      this.init();
    }

    setup() {
      for (let i = 0; i < this.DOM.forms.length; i++) {
        this.instances.push(new ProductForm(this.DOM.forms[i]));
      }
    }

    destroy() {
      if (this.DOM) {
        for (let i = 0; i < this.instances.length; i++) {
          this.instances[i].destroy();
        }
        this.instances = [];
        this.DOM = undefined;
      }
    }

    reinit() {
      this.destroy();
      this.init();
    }

    init(container = document) {
      const forms = container.querySelectorAll('.js-product-form');

      if (forms.length) {
        this.DOM = {};
        this.DOM.forms = forms;
        this.instances = [];

        this.setup();
      }
    }
  }

  class ProductForm {
    constructor(form) {
      this.init(form);
    }

    setup() {
      this._onSubmit = (event) => { this.onSubmit(event); };
      this.DOM.form.addEventListener('submit', this._onSubmit);
    }

    // Add-on toggles live outside the form (they can sit in any block of the
    // product section), so they are matched to it by id rather than by nesting.
    getAddOnInputs() {
      const formId = this.DOM.form.getAttribute('id');
      if (!formId) return [];

      return Array.from(
        document.querySelectorAll('.js-product-add-on[data-product-form="' + formId + '"]'),
      );
    }

    getAddOns() {
      return this.getAddOnInputs()
        .filter((input) => input.checked)
        .map((input) => ({
          id: Number(input.value),
          quantity: 1,
          properties: { _added_with: input.dataset.addedWith },
        }));
    }

    // The selection is consumed by the add to cart call, so the toggles go back
    // to their default state once the items are in the cart.
    resetAddOns() {
      this.getAddOnInputs().forEach((input) => {
        input.checked = false;
      });
    }

    getFormItem(formData) {
      const item = {
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity')) || 1,
      };

      const properties = {};
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('properties[') && key.endsWith(']')) {
          properties[key.slice(11, -1)] = value;
        }
      }
      if (Object.keys(properties).length) item.properties = properties;

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) item.selling_plan = Number(sellingPlan);

      return item;
    }

    onSubmit(event) {
      event.preventDefault();

      if (this.DOM.submit.getAttribute('aria-disabled') === 'true') return;
      if (this.DOM.submit.classList.contains('is-loading')) return;

      this.DOM.submits.forEach((btn) => btn.classList.add('is-loading'));

      const submitsArray = Array.from(this.DOM.submits);
      this._submitter = event.submitter && !submitsArray.includes(event.submitter)
        ? event.submitter
        : null;
      if (this._submitter) this._submitter.classList.add('is-loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.DOM.form);
      const addOns = this.getAddOns();

      if (addOns.length) {
        // /cart/add.js only accepts several variants at once as a JSON `items`
        // array, so the form has to be serialized by hand in this case.
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify({ items: [this.getFormItem(formData)].concat(addOns) });
      } else {
        config.body = formData;
      }

      fetch(window.routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            console.log('product-form-' + formData.get('id') + ' errors:', response.errors || response.description);

            if (!this.DOM.errorMessage) return;

            this.DOM.errorMessage.classList.toggle('is-visible', response.description);

            let errorText = '';
            if (typeof response.description == 'object') {
              for (const error in response.description) {
                if (errorText.length > 0) {
                  errorText = errorText + ', ' + response.description[error];
                } else {
                  errorText = response.description[error];
                }
              }
            } else {
              errorText = response.description;
            }

            this.DOM.errorMessage.innerText = errorText;
            const yOffset = -200;
            const y = this.DOM.errorMessage.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });

            return;
          } else {
            this.resetAddOns();

            if (window.theme && window.theme.cart_type === 'page') {
              window.location.href = window.routes.cart_url;
              return;
            }
            subEvents.publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }
        })
        .catch((e) => {
          console.error('error', e);
        })
        .finally(() => {
          if (this.DOM?.submits) this.DOM.submits.forEach((btn) => btn.classList.remove('is-loading'));
          if (this._submitter) {
            this._submitter.classList.remove('is-loading');
            this._submitter = null;
          }
        });
    }

    destroy() {
      if (this.DOM) {
        this.DOM.form.removeEventListener('submit', this._onSubmit);
        this.DOM = undefined;
      }
    }

    init(form) {
      if (form) {
        this.DOM = {};
        this.DOM.form = form;
        this.DOM.submit = this.DOM.form.querySelector('.js-product-form-submit');
        this.DOM.submits = this.DOM.form.querySelectorAll('.js-product-form-submit');
        this.DOM.errorMessage = this.DOM.form.querySelector('.js-product-form-error');

        this.setup();
      }
    }
  }

  // Add-ons with more than one purchasable variant render a small select next to
  // their title. Picking a variant rewrites the id the checkbox carries, so the
  // row adds the variant on screen, and refreshes the price shown with it.
  // Delegated on purpose: the block survives section re-renders this way.
  function onAddOnVariantChange(event) {
    const select = event.target.closest ? event.target.closest('.js-product-add-on-variant') : null;
    if (!select) return;

    const item = select.closest('.product-add-ons__item');
    const option = select.options[select.selectedIndex];
    if (!item || !option) return;

    const check = item.querySelector('.js-product-add-on');
    if (check) check.value = select.value;

    const price = item.querySelector('.js-product-add-on-price');
    if (price) price.textContent = '+' + option.dataset.price;

    const compareAt = item.querySelector('.js-product-add-on-compare-at');
    if (compareAt) {
      compareAt.textContent = option.dataset.compareAtPrice || '';
      compareAt.hidden = !option.dataset.compareAtPrice;
    }
  }

  document.addEventListener('change', onAddOnVariantChange);

  window.Universe = window.Universe || {};
  window.Universe.ProductForms = ProductForms;
  window.Universe.ProductForm = ProductForm;
})();
